import React, { useEffect, useRef, useState } from 'react';
import { trackEvent } from '../../../utils/analytics';
import './AIENoraFilm.css';

// v7 AMBIENT CUT (CEL-1680, ship-gate passed Aug 25): supersedes v6. Picture
// stream byte-identical to the locked v6 (all 8 burned-in captions unchanged);
// audio patched 27.4-35.2s so the narration says "context" per canon (was
// "contacts"), sign-off provably untouched. Poster = the onboarding beat
// (subject, not setting). VTT = v7 transcription matching the corrected audio.
const SRC = '/assets/cellcog/nora-film-web-v7.mp4';
const POSTER = '/assets/cellcog/nora-film-poster-v2.jpg';
const CAPTIONS = '/assets/cellcog/nora-film-captions-v7.vtt';

/**
 * AIENoraFilm v2: the Grok-style player (Nitish-ruled Aug 24, pattern source
 * x.ai/bot, probed live). Two layers:
 *
 * 1. AMBIENT: once the section actually scrolls into view (>=35%), a muted
 *    looping instance of the film plays inside the frame so the section feels
 *    alive; it pauses out of view and in hidden tabs. Reduced-motion visitors
 *    keep the still poster. Prerender-safe BY CONSTRUCTION: the ambient video
 *    element is mounted only on a real IntersectionObserver hit, and ReactSnap
 *    snapshots the page at the top of the document, so the prerendered HTML
 *    carries poster-only markup (same concern HomeHeroVideo.js documents).
 * 2. LIGHTBOX: clicking the frame (one big button, left-anchored "Play" like
 *    x.ai's) opens an overlay with a SECOND video element, unmuted from 0:00
 *    with native controls, Esc / scrim / X to close. Sound is a deliberate
 *    fresh start, not an unmute of the loop: the muted loop is ambience, the
 *    lightbox is the actual viewing (their trick, verified in the DOM probe).
 *    The lightbox fully UNMOUNTS on close, so no invisible scrim can ever eat
 *    page clicks (the shift-133 overlay trap).
 *
 * Analytics: cta_click{nora_film_play} = lightbox open (series-continuous with
 * v1: it still means "chose to watch with sound"). video_progress_25/50/75/100
 * fire from the LIGHTBOX instance only. nora_film_ambient_start fires once per
 * pageview on the first ambient autoplay (the cheap denominator).
 */
/**
 * @param {string} page - analytics page label (Aug 24, Nitish: the film joins
 *   the HOMEPAGE as the proof beat — /ai-employees traffic is single-digit,
 *   so its best asset was playing to an empty room). Every event stamps this,
 *   keeping the two placements separately readable in GA4/PostHog.
 */
const AIENoraFilm = ({ page = 'ai-employees' }) => {
  const frameRef = useRef(null);
  const frameBtnRef = useRef(null);
  const ambientRef = useRef(null);
  const lightboxVideoRef = useRef(null);
  const closeBtnRef = useRef(null);
  const inViewRef = useRef(false);
  const ambientStartedRef = useRef(false);
  const milestonesRef = useRef(new Set());
  const [ambientMounted, setAmbientMounted] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Drive the ambient loop from real viewport intersection + tab visibility.
  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const syncPlayback = () => {
      const v = ambientRef.current;
      if (!v) return;
      if (inViewRef.current && document.visibilityState === 'visible') {
        v.muted = true; // belt and braces: React's muted prop sets the property, not the attribute
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          inViewRef.current = e.isIntersecting && e.intersectionRatio >= 0.35;
          if (inViewRef.current) setAmbientMounted(true);
          syncPlayback();
        });
      },
      { threshold: [0, 0.35] }
    );
    io.observe(el);
    document.addEventListener('visibilitychange', syncPlayback);
    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', syncPlayback);
    };
  }, []);

  // Ambient element mounts after an intersection hit: start it immediately.
  useEffect(() => {
    if (!ambientMounted) return;
    const v = ambientRef.current;
    if (v && inViewRef.current && document.visibilityState === 'visible') {
      v.muted = true;
      v.play().catch(() => {});
    }
  }, [ambientMounted]);

  const onAmbientPlay = () => {
    if (ambientStartedRef.current) return;
    ambientStartedRef.current = true;
    trackEvent('nora_film_ambient_start', { page, location: 'nora_film' });
  };

  const openLightbox = () => {
    setLightboxOpen(true);
    trackEvent('cta_click', { cta_name: 'nora_film_play', page, location: 'nora_film' });
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    if (frameBtnRef.current) frameBtnRef.current.focus();
    const v = ambientRef.current;
    if (v && inViewRef.current && document.visibilityState === 'visible') v.play().catch(() => {});
  };

  // Lightbox lifecycle: scroll lock, Esc, focus, pause the ambient underneath.
  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // One ask at a time (as HomeVideoTile): the mobile sticky CTA and the support chat step aside while the film plays.
    document.body.classList.add('cc-modal-open');
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
    };
    document.addEventListener('keydown', onKey);
    if (ambientRef.current) ambientRef.current.pause();
    if (closeBtnRef.current) closeBtnRef.current.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('cc-modal-open');
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen]);

  const onScrimClick = (e) => {
    if (e.target === e.currentTarget) closeLightbox();
  };

  const onTime = () => {
    const v = lightboxVideoRef.current;
    if (!v || !v.duration) return;
    const pct = (v.currentTime / v.duration) * 100;
    [25, 50, 75, 99].forEach((m) => {
      if (pct >= m && !milestonesRef.current.has(m)) {
        milestonesRef.current.add(m);
        trackEvent(`video_progress_${m === 99 ? 100 : m}`, {
          video: 'nora-film',
          page,
          location: 'nora_film',
        });
      }
    });
  };

  return (
    <section className="aie-nora" aria-label="Watch one of them work">
      <div className="aie-nora__inner">
        <h2 className="aie-nora__title">Watch one of them work</h2>
        <p className="aie-nora__sub">
          Nora is a marketing employee. This is what her work looks like from the day she was hired.
        </p>
        <div className="aie-nora__frame" ref={frameRef}>
          <button
            type="button"
            ref={frameBtnRef}
            className="aie-nora__frameBtn"
            onClick={openLightbox}
            aria-label="Play the film with sound: watch Nora work"
          >
            <img
              className="aie-nora__poster"
              src={POSTER}
              alt="A studio owner reviews plans with clients while Nora's marketing updates arrive"
              loading="lazy"
            />
            {ambientMounted && (
              <video
                ref={ambientRef}
                className="aie-nora__ambient"
                src={SRC}
                muted
                loop
                playsInline
                preload="metadata"
                aria-hidden="true"
                tabIndex={-1}
                onPlay={onAmbientPlay}
              />
            )}
            <span className="aie-nora__play" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="currentColor" className="aie-nora__playIcon">
                <path d="M8 5.14v13.72c0 .8.87 1.3 1.56.89l11.02-6.86a1.05 1.05 0 0 0 0-1.78L9.56 4.25A1.05 1.05 0 0 0 8 5.14Z" />
              </svg>
              Play
            </span>
            <span className="aie-nora__badge">1:32</span>
          </button>
        </div>
      </div>
      {lightboxOpen && (
        <div
          className="aie-nora__lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Nora film player"
          onClick={onScrimClick}
        >
          <div className="aie-nora__lightboxInner">
            <button
              type="button"
              ref={closeBtnRef}
              className="aie-nora__close"
              onClick={closeLightbox}
              aria-label="Close the film"
            >
              &#10005;
            </button>
            <video
              ref={lightboxVideoRef}
              className="aie-nora__lightboxVideo"
              src={SRC}
              poster={POSTER}
              controls
              autoPlay
              playsInline
              preload="auto"
              crossOrigin="anonymous"
              onTimeUpdate={onTime}
              aria-label="Nora, an AI marketing employee: her work from the day she was hired"
            >
              <track kind="captions" src={CAPTIONS} srcLang="en" label="English" />
            </video>
          </div>
        </div>
      )}
    </section>
  );
};

export default AIENoraFilm;
